import * as moment from 'moment-timezone'
import {
  Column,
  FilterOption,
  FilterValue,
  ProjectGroupProps,
  SavedFilter,
  SavedFilterData,
  TicketRow,
} from '../types'
import { SYSTEM_DATE_KEYS, SYSTEM_DATETIME_KEYS } from '../cell_renderers/types'

export const NON_FILTERABLE_KEYS = ['uploads', 'design_files', 'comment_attachments', 'attachments', 'element_placements']
export const SYSTEM_TEXT_KEYS = [
  'id',
  'title',
  'description',
  'location_name',
  'location_number',
  'order_number',
  'job_number',
  'purchase_order_number',
  'nav_project_id',
  'nav_project_manager_username',
]

const ASSOCIATION_SINGLE_KEYS = ['project_manager', 'customer_responsible', 'location_manager', 'region']
const ASSOCIATION_MULTI_KEYS = ['service_companies', 'installers', 'project_tags', 'elements', 'element_groups']

export const COMPANION_KEY_MAP: Record<string, string> = {
  customer_responsible: 'customer_responsible_id',
  project_manager: 'project_manager_id',
  location_manager: 'location_manager_id',
  region: 'region_id',
  service_companies: 'service_company_ids',
  installers: 'installer_ids',
  project_tags: 'project_tag_ids',
  elements: 'element_ids',
  element_groups: 'element_group_ids',
}

type AssociationOptionsProp = {
  [K in keyof ProjectGroupProps]: ProjectGroupProps[K] extends FilterOption[] ? K : never
}[keyof ProjectGroupProps]

export const ASSOCIATION_OPTIONS_PROP_MAP: Record<string, AssociationOptionsProp> = {
  customer_responsible: 'customer_responsible_users',
  project_manager: 'available_project_managers',
  location_manager: 'available_location_managers',
  region: 'available_regions',
  service_companies: 'available_service_companies',
  installers: 'available_installers',
  project_tags: 'available_project_tags',
  elements: 'available_elements',
  element_groups: 'available_element_groups',
}

export type FilterType =
  | 'select'
  | 'select_id'
  | 'association_single'
  | 'association_multi'
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'not_filterable'

const normalizeArrayValue = <T extends string | number>(value: T[]): T[] =>
  [...value].sort((left, right) => String(left).localeCompare(String(right)))

const isNumberRangeValue = (
  value: FilterValue,
): value is { min?: number; max?: number } => !Array.isArray(value) && typeof value === 'object' && ('min' in value || 'max' in value)

const isDateRangeValue = (
  value: FilterValue,
): value is { from?: string; to?: string } => !Array.isArray(value) && typeof value === 'object' && ('from' in value || 'to' in value)

const compactRangeValue = (value: { min?: number; max?: number } | { from?: string; to?: string }) => {
  if ('min' in value || 'max' in value) {
    const compactValue: { min?: number; max?: number } = {}
    if (typeof value.min === 'number' && !Number.isNaN(value.min)) compactValue.min = value.min
    if (typeof value.max === 'number' && !Number.isNaN(value.max)) compactValue.max = value.max
    return compactValue
  }

  const compactValue: { from?: string; to?: string } = {}
  if (value.from) compactValue.from = value.from
  if (value.to) compactValue.to = value.to
  return compactValue
}

export const getFilterType = (column: Column): FilterType => {
  if (column.key === 'status') return 'select'
  if (NON_FILTERABLE_KEYS.includes(column.key)) return 'not_filterable'
  if (ASSOCIATION_SINGLE_KEYS.includes(column.key)) return 'association_single'
  if (ASSOCIATION_MULTI_KEYS.includes(column.key)) return 'association_multi'
  if (column.type === 'Projects::Columns::Select') return 'select_id'
  if (column.type === 'Projects::Columns::Text') return 'text'
  if (column.type === 'Projects::Columns::Number') return 'number'
  if (column.type === 'Projects::Columns::Date') return 'date'
  if (SYSTEM_DATETIME_KEYS.includes(column.key)) return 'datetime'
  if (SYSTEM_DATE_KEYS.includes(column.key)) return 'date'
  if (SYSTEM_TEXT_KEYS.includes(column.key)) return 'text'
  return 'not_filterable'
}

export const isFilterableColumn = (column: Column): boolean => getFilterType(column) !== 'not_filterable'

export const isEmptyFilter = (value: FilterValue | undefined): boolean => {
  if (value == null) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'string') return value.trim() === ''
  if (isNumberRangeValue(value)) return value.min == null && value.max == null
  if (isDateRangeValue(value)) return !value.from && !value.to
  return true
}

export const compactFilterData = (filters: SavedFilterData): SavedFilterData =>
  Object.entries(filters).reduce<SavedFilterData>((result, [key, value]) => {
    if (isEmptyFilter(value)) return result

    if (Array.isArray(value)) {
      result[key] = value.every((entry) => typeof entry === 'number')
        ? normalizeArrayValue(value as number[])
        : normalizeArrayValue(value as string[])
      return result
    }

    if (typeof value === 'string') {
      result[key] = value.trim()
      return result
    }

    if (isNumberRangeValue(value) || isDateRangeValue(value)) {
      result[key] = compactRangeValue(value)
    }
    return result
  }, {})

export const filterValuesEqual = (left?: FilterValue, right?: FilterValue): boolean => {
  if (isEmptyFilter(left) && isEmptyFilter(right)) return true
  if (left == null || right == null) return false

  if (Array.isArray(left) && Array.isArray(right)) {
    return JSON.stringify(normalizeArrayValue(left)) === JSON.stringify(normalizeArrayValue(right))
  }

  if (typeof left === 'string' && typeof right === 'string') {
    return left.trim() === right.trim()
  }

  if ((isNumberRangeValue(left) || isDateRangeValue(left)) && (isNumberRangeValue(right) || isDateRangeValue(right))) {
    return JSON.stringify(compactRangeValue(left)) === JSON.stringify(compactRangeValue(right))
  }

  return false
}

export const filtersExactMatch = (left: SavedFilterData, right: SavedFilterData): boolean => {
  const compactLeft = compactFilterData(left)
  const compactRight = compactFilterData(right)
  const keys = Array.from(new Set([...Object.keys(compactLeft), ...Object.keys(compactRight)]))

  return keys.every((key) => filterValuesEqual(compactLeft[key], compactRight[key]))
}

export const filtersMatch = (saved: SavedFilterData, current: SavedFilterData): boolean =>
  Object.entries(compactFilterData(saved)).every(([key, value]) => filterValuesEqual(value, compactFilterData(current)[key]))

export const findMatchingFilter = (savedFilters: SavedFilter[], currentFilter: SavedFilterData): SavedFilter | undefined => {
  const exactMatch = savedFilters.find((filter) => filtersExactMatch(filter.filter, currentFilter))
  if (exactMatch) return exactMatch

  return savedFilters.find((filter) => filtersMatch(filter.filter, currentFilter))
}

const getTicketFilterMetaValue = (ticket: TicketRow, key: string) => ticket.filter_meta?.[key]

const intersectsWith = (selected: number[], actual: number[]) => selected.some((value) => actual.includes(value))

export const matchesFilter = (
  ticket: TicketRow,
  columnKey: string,
  value: FilterValue,
  column: Column,
  timeZone?: string,
): boolean => {
  switch (getFilterType(column)) {
    case 'select': {
      const ticketValue = ticket.values[columnKey]
      return typeof ticketValue === 'string' && (value as string[]).includes(ticketValue)
    }
    case 'select_id': {
      const ticketValue = getTicketFilterMetaValue(ticket, `${columnKey}_option_ids`)
      const selectedIds = value as number[]
      if (Array.isArray(ticketValue)) return intersectsWith(selectedIds, ticketValue)
      return typeof ticketValue === 'number' && selectedIds.includes(ticketValue)
    }
    case 'association_single': {
      const ticketValue = getTicketFilterMetaValue(ticket, COMPANION_KEY_MAP[columnKey])
      return typeof ticketValue === 'number' && (value as number[]).includes(ticketValue)
    }
    case 'association_multi': {
      const ticketValue = getTicketFilterMetaValue(ticket, COMPANION_KEY_MAP[columnKey])
      return Array.isArray(ticketValue) && intersectsWith(value as number[], ticketValue)
    }
    case 'text': {
      const ticketValue = ticket.values[columnKey]
      return String(ticketValue ?? '').toLowerCase().includes((value as string).trim().toLowerCase())
    }
    case 'number': {
      const ticketValue = Number(ticket.values[columnKey])
      if (Number.isNaN(ticketValue)) return false

      const bounds = value as { min?: number; max?: number }
      if (typeof bounds.min === 'number' && ticketValue < bounds.min) return false
      if (typeof bounds.max === 'number' && ticketValue > bounds.max) return false
      return true
    }
    case 'date': {
      const ticketValue = String(ticket.values[columnKey] ?? '')
      if (!ticketValue) return false

      const bounds = value as { from?: string; to?: string }
      if (bounds.from && ticketValue < bounds.from) return false
      if (bounds.to && ticketValue > bounds.to) return false
      return true
    }
    case 'datetime': {
      const ticketValue = String(ticket.values[columnKey] ?? '')
      if (!ticketValue) return false

      const zone = timeZone || moment.tz.guess()
      const ticketMoment = moment.tz(ticketValue, zone)
      if (!ticketMoment.isValid()) return false

      const bounds = value as { from?: string; to?: string }
      if (bounds.from && ticketMoment.valueOf() < moment.tz(bounds.from, zone).startOf('day').valueOf()) return false
      if (bounds.to && ticketMoment.valueOf() > moment.tz(bounds.to, zone).endOf('day').valueOf()) return false
      return true
    }
    case 'not_filterable':
      return true
    default:
      return true
  }
}

export const hasFilterNamespace = (search: string = window.location.search): boolean =>
  search.includes('filter%5B') || search.includes('filter[')

const getColumnByKey = (columns: Column[], key: string) => columns.find((column) => column.key === key)

const shouldParseNumericArray = (key: string, column?: Column) => {
  if (key === 'customer_responsible') return true
  if (!column) return false

  return ['association_single', 'association_multi', 'select_id'].includes(getFilterType(column))
}

export const parseFilterParams = (columns: Column[], search: string = window.location.search): SavedFilterData | null => {
  if (!hasFilterNamespace(search)) return null

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const keys = Array.from(new Set(Array.from(params.keys()).map((key) => key.match(/^filter\[([^\]]+)\]/)?.[1]).filter(
    (key): key is string => Boolean(key) && key !== 'active'
  )))

  return keys.reduce<SavedFilterData>((result, key) => {
    const arrayValues = params.getAll(`filter[${key}][]`)
    if (arrayValues.length > 0) {
      const column = getColumnByKey(columns, key)
      result[key] = shouldParseNumericArray(key, column)
        ? arrayValues.map((entry) => Number(entry)).filter((entry) => !Number.isNaN(entry))
        : arrayValues
      return result
    }

    const min = params.get(`filter[${key}][min]`)
    const max = params.get(`filter[${key}][max]`)
    if (min !== null || max !== null) {
      const rangeValue: { min?: number; max?: number } = {}
      if (min !== null && min !== '') rangeValue.min = Number(min)
      if (max !== null && max !== '') rangeValue.max = Number(max)
      result[key] = rangeValue
      return result
    }

    const from = params.get(`filter[${key}][from]`)
    const to = params.get(`filter[${key}][to]`)
    if (from !== null || to !== null) {
      result[key] = {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      }
      return result
    }

    const plainValue = params.get(`filter[${key}]`)
    if (plainValue !== null) result[key] = plainValue
    return result
  }, {})
}

export const buildFilterSearchParams = (filters: SavedFilterData): URLSearchParams => {
  const params = new URLSearchParams()
  const compactFilters = compactFilterData(filters)

  Object.entries(compactFilters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => params.append(`filter[${key}][]`, String(entry)))
      return
    }

    if (typeof value === 'string') {
      params.set(`filter[${key}]`, value)
      return
    }

    if (isNumberRangeValue(value)) {
      if (typeof value.min === 'number') params.set(`filter[${key}][min]`, String(value.min))
      if (typeof value.max === 'number') params.set(`filter[${key}][max]`, String(value.max))
      return
    }

    if (isDateRangeValue(value)) {
      if (value.from) params.set(`filter[${key}][from]`, value.from)
      if (value.to) params.set(`filter[${key}][to]`, value.to)
    }
  })

  if (Array.from(params.keys()).length === 0) {
    params.set('filter[active]', '1')
  }

  return params
}
